/**
 * Builds the community-specific Vision AI request-context filters.
 *
 * Owns its data sources (route, side-panel filters, stores) and exposes a
 * reactive `requestContextFilters` computed that produces the transformed
 * filter object the Vision AI backend expects.
 */

import { useFilterSidePanel } from '@dashhudson/dashing-ui/filter-side-panel';
import intersection from 'lodash/intersection';
import { computed, toValue } from 'vue';
import { useRoute } from 'vue-router';

import { useCommunityFilters } from '@/app/community/composables/useCommunityFilters';
import { COMMUNITY_ROUTE_NAMES } from '@/app/community/constants';
import { MESSAGE_TYPE_FILTER_OPTIONS } from '@/app/community/constants/communityFilters';
import { useCommunityMultiBrandsStore } from '@/stores/community-multi-brands';
import { useIdentityStore } from '@/stores/identity';

interface SourceUpdatedFilter {
  range?: { start?: string; end?: string };
}

// The community filter UI uses one vocabulary for message types (e.g. 'CONVERSATION'),
// but vision-ai-backend expects the stats vocabulary (e.g. 'DMS'). Each filter option
// carries both as `.value` and `.statsValue` respectively.
export function translateMessageTypesToStatsValue(messageTypes: string[]): string[] {
  if (!messageTypes?.length) return [];

  return messageTypes.map((messageTypeValue) => {
    const messageTypeKey = messageTypeValue as keyof typeof MESSAGE_TYPE_FILTER_OPTIONS;
    const messageTypeOption = MESSAGE_TYPE_FILTER_OPTIONS[messageTypeKey];
    return messageTypeOption?.statsValue ?? messageTypeValue;
  });
}

export function useCommunityVisionAiRequestContext() {
  const route = useRoute();
  const { scopedFilters } = useFilterSidePanel();
  const identityStore = useIdentityStore();
  const communityMultiBrandsStore = useCommunityMultiBrandsStore();
  const { getTypesFromMessageTypes, getTypesFromPlatforms } = useCommunityFilters();

  const requestContextFilters = computed<Record<string, unknown>>(() => {
    const rawFilters = toValue(scopedFilters.value) as Record<string, unknown>;
    if (!rawFilters) return {};

    const sourceUpdated = rawFilters.sourceUpdated as SourceUpdatedFilter | undefined;
    const platforms = (rawFilters.platforms as string[]) ?? [];
    const messageTypes = (rawFilters.messageTypes as string[]) ?? [];

    // This returns all interaction types for the given platforms
    const interactionTypesFromPlatforms = getTypesFromPlatforms(platforms);
    // This returns all interaction types for the given message types.
    // We cannot use this value immediately, because it could contain
    // channel-specific interaction types that should be filtered out by the above filtered platforms.
    const interactionTypesFromMessageTypes = getTypesFromMessageTypes(messageTypes);
    // We now form the final interaction types we should filter by
    const filteredInteractionTypes = interactionTypesFromMessageTypes.length
      ? intersection(interactionTypesFromPlatforms, interactionTypesFromMessageTypes)
      : interactionTypesFromPlatforms;

    // We need to convert brand tags, into a list of brand IDs before sending to the backend.
    const brandTagIds = (rawFilters.brandTagIds as number[]) ?? [];
    const brandIdsFromTags = brandTagIds.length
      ? communityMultiBrandsStore.getBrandIdsFromBrandTagIds(brandTagIds)
      : [];
    const explicitBrandIds = (rawFilters.brandIds as number[]) ?? [];
    const resolvedBrandIds = brandIdsFromTags.length ? brandIdsFromTags : explicitBrandIds;

    // Combine tag IDs and operator into the prefixed format the community backend
    // expects (e.g. "AND:1,2,3"), so vision-ai can pass it through as-is.
    const tagIds = rawFilters.tagIds as number[] | undefined;
    const tagOperator = (rawFilters.tagOperator as string) ?? 'OR';
    const messageTags =
      tagIds && tagIds.length > 0 ? `${tagOperator}:${tagIds.join(',')}` : undefined;

    const filters: Record<string, unknown> = {
      messageTags,
      sourceUpdatedAfter: sourceUpdated?.range?.start,
      sourceUpdatedBefore: sourceUpdated?.range?.end,
      platforms: rawFilters.platforms,
      messageTypes: translateMessageTypesToStatsValue(messageTypes),
      messageState: rawFilters.messageState,
      messageContent: rawFilters.messageContent,
      sentiments: rawFilters.sentiments,
      assigneeIds: rawFilters.assigneeIds,
      brandIds: resolvedBrandIds,
      interactionRuleIds: rawFilters.interactionRuleIds,
      mediaIds: rawFilters.mediaIds,
      query: route.query?.query || undefined,
      interactionTypes: filteredInteractionTypes,
    };

    if (route.name === COMMUNITY_ROUTE_NAMES.ASSIGNED_TO_YOU && identityStore.identity?.id) {
      filters.assigneeIds = [identityStore.identity.id];
    }

    return filters;
  });

  return { requestContextFilters };
}
